import React, { useState, useRef, useEffect } from 'react';
import { FiMessageCircle, FiX, FiSend } from 'react-icons/fi';
import API from '../api/axios';
import './AIBubble.css';

const renderTextWithLinks = (text) => {
  if (!text) return '';
  const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)]+)/g;
  const elements = [];
  let lastOffset = 0;
  let keyIndex = 0;
  
  const textStr = String(text);
  textStr.replace(regex, (match, label, mdUrl, rawUrl, offset) => {
    if (offset > lastOffset) {
      elements.push(textStr.substring(lastOffset, offset));
    }
    
    if (label && mdUrl) {
      elements.push(
        <a 
          key={`link-${keyIndex++}`} 
          href={mdUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          style={{ color: '#60a5fa', textDecoration: 'underline', fontWeight: '600' }}
        >
          {label}
        </a>
      );
    } else if (rawUrl) {
      elements.push(
        <a 
          key={`link-${keyIndex++}`} 
          href={rawUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          style={{ color: '#60a5fa', textDecoration: 'underline', fontWeight: '600' }}
        >
          {rawUrl}
        </a>
      );
    }
    
    lastOffset = offset + match.length;
    return match;
  });
  
  if (lastOffset < textStr.length) {
    elements.push(textStr.substring(lastOffset));
  }
  
  return elements.length > 0 ? elements : text;
};

const AIBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Namaste! 🙏 I\'m Shubink AI. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Position & Dragging States
  const [position, setPosition] = useState({ x: window.innerWidth - 72, y: window.innerHeight - 136 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  
  const dragStart = useRef({ x: 0, y: 0 });
  const offset = useRef({ x: 0, y: 0 });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Position initialized dynamically on load
  useEffect(() => {
    const handleInitialPosition = () => {
      setPosition({ x: window.innerWidth - 72, y: window.innerHeight - 136 });
    };
    handleInitialPosition();
    window.addEventListener('resize', handleInitialPosition);
    return () => {
      window.removeEventListener('resize', handleInitialPosition);
    };
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const { data } = await API.post('/ai/chat', {
        message: userMessage,
        history: messages.slice(-10)
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I\'m having trouble connecting. Please try again!' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Dragging event handlers
  const positionRef = useRef(position);
  const isDraggingRef = useRef(isDragging);
  const hasMovedRef = useRef(hasMoved);

  // Keep refs in sync with state updates to prevent stale closures
  useEffect(() => { positionRef.current = position; }, [position]);
  useEffect(() => { isDraggingRef.current = isDragging; }, [isDragging]);
  useEffect(() => { hasMovedRef.current = hasMoved; }, [hasMoved]);

  const handleStart = (clientX, clientY) => {
    dragStart.current = { x: clientX, y: clientY };
    offset.current = { x: positionRef.current.x, y: positionRef.current.y };
    isDraggingRef.current = true;
    setIsDragging(true);
    hasMovedRef.current = false;
    setHasMoved(false);
  };

  const isTouchRef = useRef(false);

  const handleMouseDown = (e) => {
    if (isTouchRef.current) return;
    if (e.button !== 0) return; // Only left click
    handleStart(e.clientX, e.clientY);
  };

  const handleTouchStart = (e) => {
    isTouchRef.current = true;
    if (e.touches && e.touches[0]) {
      handleStart(e.touches[0].clientX, e.touches[0].clientY);
    }
    // Reset after emulated mouse events are finished (usually < 300ms)
    setTimeout(() => {
      isTouchRef.current = false;
    }, 500);
  };

  useEffect(() => {
    const handleMove = (clientX, clientY) => {
      if (!isDraggingRef.current) return;
      
      const deltaX = clientX - dragStart.current.x;
      const deltaY = clientY - dragStart.current.y;
      
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        if (!hasMovedRef.current) {
          hasMovedRef.current = true;
          setHasMoved(true);
        }
      }

      const newX = offset.current.x + deltaX;
      const newY = offset.current.y + deltaY;

      // Bound within viewport limits
      const boundedX = Math.max(10, Math.min(newX, window.innerWidth - 66));
      const boundedY = Math.max(10, Math.min(newY, window.innerHeight - 80));

      setPosition({ x: boundedX, y: boundedY });
    };

    const onMouseMove = (e) => {
      handleMove(e.clientX, e.clientY);
    };

    const onTouchMove = (e) => {
      if (e.touches && e.touches[0]) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleEnd = () => {
      if (!isDraggingRef.current) return;
      
      isDraggingRef.current = false;
      setIsDragging(false);
      
      if (hasMovedRef.current) {
        // Snap smoothly to the left or right edge
        const currentPos = positionRef.current;
        const leftDist = currentPos.x;
        const rightDist = window.innerWidth - 56 - currentPos.x;
        const snapX = leftDist < rightDist ? 16 : window.innerWidth - 56 - 16;
        setPosition({ x: snapX, y: currentPos.y });
      } else {
        // It was a click!
        setIsOpen(prev => !prev);
      }
      
      hasMovedRef.current = false;
      setHasMoved(false);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, []);

  // Dynamic window alignment: open below the bubble if bubble is in the upper half of screen
  const isUpperHalf = position.y < window.innerHeight / 2;
  const chatWindowStyle = isOpen ? {
    position: 'fixed',
    left: position.x < window.innerWidth / 2 ? Math.max(12, position.x) : undefined,
    right: position.x >= window.innerWidth / 2 ? Math.max(12, window.innerWidth - position.x - 56) : undefined,
    bottom: !isUpperHalf ? window.innerHeight - position.y + 10 : undefined,
    top: isUpperHalf ? position.y + 66 : undefined,
    zIndex: 9999,
  } : {};

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div className="ai-chat-window slide-up" style={chatWindowStyle}>
          <div className="ai-chat-header">
            <div className="ai-chat-header-info">
              <div className="ai-avatar">🤖</div>
              <div>
                <div className="ai-chat-title">Shubink AI</div>
                <div className="ai-chat-status">Always here to help</div>
              </div>
            </div>
            <button className="modal-close" onClick={() => setIsOpen(false)}>
              <FiX />
            </button>
          </div>

          <div className="ai-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`ai-msg ${msg.role}`}>
                <div className="ai-msg-bubble">
                  {renderTextWithLinks(msg.content)}
                </div>
              </div>
            ))}
            {loading && (
              <div className="ai-msg assistant">
                <div className="ai-msg-bubble ai-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="ai-input-area">
            <input
              type="text"
              placeholder="Ask me anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              maxLength={500}
              className="ai-input"
            />
            <button 
              className="ai-send-btn" 
              onClick={sendMessage}
              disabled={!input.trim() || loading}
            >
              <FiSend />
            </button>
          </div>
        </div>
      )}

      {/* Floating Bubble */}
      <button 
        className={`ai-bubble ${isOpen ? 'active' : ''} ${isDragging ? 'dragging' : ''}`}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          bottom: 'auto',
          right: 'auto',
          zIndex: 99999,
          // Disable CSS transitions during drag moves so it stutters 0%
          transition: isDragging ? 'none' : 'left 0.3s cubic-bezier(0.19, 1, 0.22, 1), top 0.3s cubic-bezier(0.19, 1, 0.22, 1), transform 0.2s',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
      >
        {isOpen ? <FiX /> : <FiMessageCircle />}
      </button>
    </>
  );
};

export default AIBubble;
